import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ProfileService, Profile, Client } from 'core/index';
import { DashboardWidgetsService } from '../services/dashboard-widgets.service';
import { DashboardService } from '@widgets/services/dashboard.service';
import { Dashboard } from '@widgets/models/Dashboard';
import { DashboardWidgets } from '@widgets/widget';
import { ActivatedRoute } from '@angular/router';
import { LAST_MONTH_DATE_FILTER, LAST_MONTH_TIME_PERIOD } from '@widgets/assets/constants';

@Component({
  selector: 'lms-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  @Input() client?: Client;
  public clientSelectorTerm = new Subject<string[]>();
  public user: Profile;
  public dashboardWidgets: DashboardWidgets = null;
  public dashboardData: Dashboard;

  public clients: any[];
  public selectedClientsIds = new Set<string>();
  public showClientsSelector: boolean = false;

  constructor(
    private profileService: ProfileService,
    private widgetsService: DashboardWidgetsService,
    private dashboardService: DashboardService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute
  ) {
    this.profileService.getLoggedUser$().subscribe((user) => {
      this.user = user;
    });
    this.clientSelectorTerm
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(
          (prev, curr) => curr.length === prev.length && curr.every((clientId) => prev.includes(clientId))
        )
      )
      .subscribe(async (clientIds: string[]) => {
        this.refreshDashboard(clientIds);
      });
  }

  public ngOnInit(): void {
    this.activatedRoute.data.subscribe((data) => {
      this.client = this.client ? this.client : data['client'];
      this.clients = this.client ? [this.client] : [];

      if (this.user && this.user.roleOptions.scope === 'client') {
        this.clients = this.user.clients.map(({ clientId: id, name, subscriptionTypeCredits, isScormClient }) => ({
          id,
          name,
          scorm: { enabled: isScormClient },
          subscriptions: {
            credits: subscriptionTypeCredits,
            users: !subscriptionTypeCredits
          }
        }));
      }
      this.setDashboardData(data.dashboard);
      this.setDashboardWidgets(this.client);
    });
  }

  public ngOnDestroy(): void {
    this.clientSelectorTerm.unsubscribe();
  }

  /**
   * Get the widgets for the dashboard
   * @param dashboard
   * @private
   */
  private setDashboardData(backendData: any): void {
    if (!backendData) {
      return null;
    }
    const {
      clientIds = [],
      groups = { empty: true },
      hours = { empty: true },
      metrics = { empty: true },
      seats = { empty: true },
      credits = { empty: true },
      courses = { empty: true },
      comments = { empty: true },
      activation = { empty: true },
      autoevaluations = { empty: true },
      nps = { empty: true },
      applicability = { empty: true },
      metadata
    } = backendData;

    const managedAccounts = this.clients;

    clientIds.forEach((clientId) => this.selectedClientsIds.add(clientId));

    const hasCreditsClients = managedAccounts.some(
      (account) => account.subscriptions.credits && clientIds.includes(account.id)
    );
    const hasSeatsClients = managedAccounts.some(
      (account) => account.subscriptions.users && clientIds.includes(account.id)
    );

    this.dashboardData = {
      clientIds,
      managedAccounts,
      credits: hasCreditsClients ? credits : null,
      seats: hasSeatsClients ? seats : null,
      scheduledClass: groups,
      trainingHours: hours,
      metricData: metrics,
      metricNps: nps,
      activation: activation,
      autoevaluations: autoevaluations,
      coursesAutoevaluations: autoevaluations?.courses || autoevaluations,
      studentCourses: courses?.topCourses,
      studentComments: comments,
      metricApplicability: applicability,
      metadata
    };
  }

  /**
   * Draw the widgets
   * @private
   */
  private setDashboardWidgets(selectedClient: Client): void {
    if (this.user) {
      this.dashboardWidgets = this.widgetsService.getWidgets(this.user, this.dashboardData, selectedClient);
    }
  }

  /**
   * Dashboard clients selected
   * Client admin
   */

  private refreshDashboard(clientIds: string[]) {
    if (clientIds && clientIds.length) {
      this.dashboardService
        .getClientsDashboardData(clientIds, LAST_MONTH_DATE_FILTER, LAST_MONTH_TIME_PERIOD)
        .subscribe((dashboardData) => {
          this.setDashboardData(dashboardData);
          this.setDashboardWidgets(this.client);
        });
    } else {
      // send void data when clientIds is empty
      this.setDashboardData({});
      this.setDashboardWidgets(this.client);
    }
  }

  public selectClient(event, clientId) {
    if (event) {
      this.selectedClientsIds.add(clientId);
    } else {
      this.selectedClientsIds.delete(clientId);
    }
    this.clientSelectorTerm.next([...this.selectedClientsIds]);
  }

  public toggleSelectAll(event) {
    if (event) {
      this.clients.forEach((client) => this.selectedClientsIds.add(client.id));
    } else {
      this.selectedClientsIds.clear();
    }
    this.clientSelectorTerm.next([...this.selectedClientsIds]);
  }

  get selectedClientsNames() {
    switch (this.allClientsSelected) {
      case 'all':
        return this.translateService.instant('actions.selectedAllClients');
      case 'none':
        return this.translateService.instant('actions.selectClient');
      case 'any':
        return this.clients
          .filter((client) => this.selectedClientsIds.has(client.id))
          .map((client) => client.name)
          .join(',');
    }
  }

  get allClientsSelected() {
    return this.selectedClientsIds.size === this.clients.length
      ? 'all'
      : this.selectedClientsIds.size === 0
      ? 'none'
      : 'any';
  }
}
